import { ref, computed } from 'vue'
import { apiService } from '../services/api'
import { authService } from '../services/auth'
import { useShopStore } from '../stores/shop-store'

export interface SetupStatus {
  cogs: boolean
  shipping_cost: boolean
  transaction_fee: boolean
  ad_spend: boolean
  custom_cost: boolean
  multi_store: boolean
}

export interface SetupStatusResponse {
  shopId: number
  setupStatus: SetupStatus
  completionPercent: number
  profitConfidenceScore: number
  missing: string[]
  revenueHigh?: boolean
}

// Shared state across all instances
const sharedSetupStatus = ref<SetupStatus | null>(null)
const sharedCompletionPercent = ref<number>(0)
const sharedProfitConfidenceScore = ref<number>(0)
const sharedMissing = ref<string[]>([])
const sharedRevenueHigh = ref<boolean>(false)
const sharedLoading = ref<boolean>(false)
const sharedError = ref<string | null>(null)

export function useSetupStatus() {
  // Use shared state so all components using this composable share the same data
  const setupStatus = sharedSetupStatus
  const completionPercent = sharedCompletionPercent
  const profitConfidenceScore = sharedProfitConfidenceScore
  const missing = sharedMissing
  const revenueHigh = sharedRevenueHigh
  const loading = sharedLoading
  const error = sharedError

  const fetchSetupStatus = async (): Promise<void> => {
    try {
      loading.value = true
      error.value = null

      // Check if there's a selected shop before making API call
      const currentShopId = authService.getShopId()
      if (!currentShopId) {
        // Reset state when no shop is selected
        setupStatus.value = null
        completionPercent.value = 0
        profitConfidenceScore.value = 0
        missing.value = []
        revenueHigh.value = false
        loading.value = false
        return
      }

      // shopId is optional since x-shop-id header is handled by apiService
      const response: SetupStatusResponse = await apiService.get('/shops/setup-status')

      const shopStore = useShopStore()

      // NOTE: fetch should be read-only. Some callers mount this composable in multiple places.
      // If the shop is linked to an account, treat multi_store as complete in the UI without patching the backend
      // to avoid fetch→patch→fetch loops.
      const ownedAccount = Boolean(shopStore.shopDetails?.owned_account)
      const normalizedSetupStatus: SetupStatus = {
        ...response.setupStatus,
        ...(ownedAccount ? { multi_store: true } : null),
      }

      setupStatus.value = normalizedSetupStatus
      completionPercent.value = response.completionPercent
      profitConfidenceScore.value = response.profitConfidenceScore
      missing.value = response.missing || []
      revenueHigh.value = response.revenueHigh || false
    } catch (err: any) {
      error.value = err.message || 'Failed to fetch setup status'
      throw err
    } finally {
      loading.value = false
    }
  }

  const refresh = async (): Promise<void> => {
    await fetchSetupStatus()
  }

  const updateSetupStatus = async (updates: Partial<SetupStatus>): Promise<void> => {
    try {
      loading.value = true
      error.value = null

      // Check if there's a selected shop before making API call
      const currentShopId = authService.getShopId()
      if (!currentShopId) {
        loading.value = false
        return
      }

      await apiService.patch('/shops/setup-status', { setupStatus: updates })

      // Refresh after update
      await fetchSetupStatus()
    } catch (err: any) {
      error.value = err.message || 'Failed to update setup status'
      throw err
    } finally {
      loading.value = false
    }
  }

  return {
    setupStatus: computed(() => setupStatus.value),
    completionPercent: computed(() => completionPercent.value),
    profitConfidenceScore: computed(() => profitConfidenceScore.value),
    missing: computed(() => missing.value),
    revenueHigh: computed(() => revenueHigh.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    fetchSetupStatus,
    refresh,
    updateSetupStatus,
  }
}
